import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Spinner from "@/v2-ui/spinner"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Placeholder from "@/v2-ui/Placeholder"
import Button from "@/v2-backoffice-client/components/button"
import { useLayerCategoryLayerQuery } from "@/v2-backoffice-client/layer/category/layer/layer.category.layer.query"
import LayerCategoryLayerAddLayerSelector from "@/v2-backoffice-client/layer/category/layer/add/LayerCategoryLayerAddLayerSelector"
import LayerCategoryLayerListItem from "@/v2-backoffice-client/layer/category/layer/LayerCategoryLayerListItem"

export type LayerCategoryLayerListProps = ComponentProps<"div"> & {
  featureCode: string
}

function LayerCategoryLayerList(props: LayerCategoryLayerListProps) {
  const { className, featureCode, ...divProps } = props
  const { data, loading, error, refetch } = useLayerCategoryLayerQuery({
    variables: { input: { categoryFeatureCode: featureCode } },
    skip: !featureCode,
  })

  const categoryLayers = data?.getLayersByCategoryCode ?? []

  return (
    <div className={cn("space-y-3", className)} {...divProps}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">
          Layers ({categoryLayers.length})
        </span>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="xs"
            iconSrc="refresh"
            onClick={() => {
              refetch()
            }}
          >
            Refresh
          </Button>

          <LayerCategoryLayerAddLayerSelector
            size="xs"
            categoryFeatureCode={featureCode}
            existingLayerCodes={categoryLayers.map((l) => l.FeatureCode)}
          />
        </div>
      </div>

      {loading ? (
        <Spinner className="m-auto relative" displaySize="sm">
          Loading layers...
        </Spinner>
      ) : error ? (
        <ErrorMessage error={error} />
      ) : categoryLayers.length === 0 ? (
        <Placeholder className="py-6">
          No layers assigned to this category.
        </Placeholder>
      ) : (
        <div className="space-y-2">
          {categoryLayers.map((layer) => (
            <LayerCategoryLayerListItem
              key={layer.FeatureCode}
              layer={layer}
              categoryFeatureCode={featureCode}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default LayerCategoryLayerList
